import { Accordion, AccordionDetails, Typography } from '@mui/material';
import { memo, useState, useMemo, useCallback } from 'react';
import { AccordionSummary } from 'shell/components/shared/summary-accordion';
import { Check, ExpandMore } from '@mui/icons-material';
import { FormikErrors, FormikTouched, useFormikContext } from 'formik';
import FormFatherhood from './form';
import {
  Parenthood,
  ParenthoodSearchForm,
  SubscriptionAccordionGenerality,
  CommonInterfaceSubscription,
} from 'subscription/view-models/common-interface-subscription';
import Close from '@mui/icons-material/Close';
import { useEventCallback } from '@mui/material';

interface FatherhoodProps {
  isCohabitation?: boolean;
  isAire?: boolean;
  isTransferNew?: boolean;
  disabled?: boolean;
}

const FatherhoodComponent = ({
  isCohabitation,
  isAire,
  isTransferNew,
  disabled,
}: FatherhoodProps): React.ReactElement => {
  const [open, setOpen] = useState(false);

  const formContext = useFormikContext<CommonInterfaceSubscription>();

  const errorFatherhood = useMemo(() => {
    return !!formContext.errors.fatherhood;
  }, [formContext.errors.fatherhood]);

  const onChange = useCallback(
    (key, value) => {
      formContext.setFieldValue('fatherhood', {
        ...(formContext.values?.fatherhood || {}),
        [key]: value,
      });
    },
    [formContext.values?.fatherhood, formContext.setFieldValue]
  );

  const onFieldGeneralityChange = useCallback(
    (key, value) => {
      onChange('generality', {
        ...(formContext.values?.fatherhood?.generality || {}),
        [key]: value,
      });
    },
    [formContext.values?.fatherhood?.generality, onChange]
  );

  const onFieldsGeneralityChange = useCallback(
    (values) => {
      onChange('generality', {
        ...(formContext.values?.fatherhood?.generality || {}),
        ...values,
      });
    },
    [formContext.values?.fatherhood?.generality, onChange]
  );

  const onFieldPersonChange = useCallback(
    (key, value) => {
      onChange('person', {
        ...(formContext.values?.fatherhood?.person || {}),
        [key]: value,
      });
    },
    [formContext.values?.fatherhood?.person, onChange]
  );

  const onFieldsPersonChange = useEventCallback((valuesNewPerson: ParenthoodSearchForm | null) => {
    if (valuesNewPerson === null) {
      onChange('person', null);
      return;
    }
    onChange('person', {
      ...(formContext.values?.fatherhood?.person || {}),
      ...valuesNewPerson,
    });
  });

  const resetForm = useCallback(() => {
    formContext.setFieldValue('fatherhood', null);
  }, [formContext.setFieldValue]);

  const onChangeValues = useCallback(
    (values) => {
      formContext.setFieldValue('fatherhood', {
        ...formContext.values.fatherhood,
        ...(values || {}),
      });
    },
    [formContext.setFieldValue, formContext.values.fatherhood]
  );

  // se solo padre e entrambi faccio vedere accordion

  return formContext.values.editDataControl?.requested === '2' ||
    formContext.values.editDataControl?.requested === '1' ||
    isAire ? (
    <Accordion
      expanded={open}
      onChange={() => {
        setOpen(!open);
      }}
    >
      <AccordionSummary
        aria-controls="panel-person-fatherhood-content"
        id="panel-person-fatherhood-header"
        expandIcon={<ExpandMore />}
      >
        {errorFatherhood ? <Close color="error" /> : <Check color="success" />}
        <Typography>Paternità</Typography>
      </AccordionSummary>
      <AccordionDetails>
        <FormFatherhood
          isCohabitation={isCohabitation}
          onChangePerson={onFieldPersonChange}
          onChangesPerson={onFieldsPersonChange}
          onChangeGenerality={onFieldGeneralityChange}
          disabled={isTransferNew || disabled}
          values={formContext.values.fatherhood}
          resetForm={resetForm}
          onChangeValues={onChangeValues}
          onChangeValuesGenerality={onFieldsGeneralityChange}
          errorsPerson={
            (formContext.errors.fatherhood as FormikErrors<Parenthood>)
              ?.person as FormikErrors<ParenthoodSearchForm>
          }
          errorsGenerality={
            (formContext.errors.fatherhood as FormikErrors<Parenthood>)
              ?.generality as FormikErrors<SubscriptionAccordionGenerality>
          }
          touchedPerson={
            (formContext.touched.fatherhood as unknown as FormikTouched<Parenthood>)
              ?.person as unknown as FormikTouched<ParenthoodSearchForm>
          }
          touchedGenerality={
            (formContext.touched.fatherhood as unknown as FormikTouched<Parenthood>)
              ?.generality as unknown as FormikTouched<SubscriptionAccordionGenerality>
          }
        />
      </AccordionDetails>
    </Accordion>
  ) : (
    <></>
  );
};

FatherhoodComponent.displayName = 'Fatherhood component';

const Fatherhood = memo(FatherhoodComponent);

export default Fatherhood;
